import React, { useState } from "react";
import { motion } from "framer-motion";

const heights = {
  short: "h-56 sm:h-64",
  mid: "h-72 sm:h-80",
  tall: "h-80 sm:h-[26rem]",
};

export default function Portfolio({ data }) {
  const [filter, setFilter] = useState("All");
  const items = (data?.items || []).filter((i) => i.enabled !== false);
  const categories = ["All", ...(data?.categories || [])];

  return (
    <section id="portfolio" data-testid="portfolio-section" className="relative py-24 sm:py-32 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            {data?.eyebrow && (
              <p className="text-[11px] uppercase tracking-[0.32em] text-[#B8941F] font-semibold mb-4">{data.eyebrow}</p>
            )}
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A0A0A]">{data?.title}</h2>
            {data?.subtitle && <p className="mt-3 text-gray-600 text-base sm:text-lg max-w-md">{data.subtitle}</p>}
          </motion.div>

          <div className="flex flex-wrap gap-2" data-testid="portfolio-filters">
            {categories.map((c) => {
              const active = filter === c;
              return (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  data-testid={`portfolio-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-4 py-2 rounded-full text-xs sm:text-sm font-medium tracking-wide transition-all border ${
                    active
                      ? "bg-[#D4AF37] text-white border-[#D4AF37] shadow-[0_8px_20px_-8px_rgba(212,175,55,0.6)]"
                      : "bg-white text-gray-700 border-gray-200 hover:border-[#D4AF37]/60 hover:text-[#0A0A0A]"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {items.map((it, i) => {
            const visible = filter === "All" || it.cat === filter;
            if (!visible) return null;
            return (
              <motion.figure
                key={it.id || `${it.title}-${i}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-5 break-inside-avoid relative group rounded-2xl overflow-hidden border border-[#EFEAD8] cursor-pointer shadow-[0_8px_24px_-12px_rgba(0,0,0,0.12)]"
                data-testid={`portfolio-item-${i}`}
              >
                <img
                  src={it.src}
                  alt={it.title}
                  className={`w-full ${heights[it.h] || heights.mid} object-cover transition-transform duration-700 group-hover:scale-110`}
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/85 via-[#0A0A0A]/20 to-transparent opacity-80 group-hover:opacity-95 transition-opacity" />
                <figcaption className="absolute inset-x-0 bottom-0 p-5 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                  <span className="inline-block text-[10px] uppercase tracking-[0.25em] text-[#F5E08C] font-bold mb-1.5">
                    {it.cat}
                  </span>
                  <h3 className="font-display text-lg sm:text-xl font-bold text-white">{it.title}</h3>
                </figcaption>
                <div className="absolute inset-0 ring-1 ring-inset ring-transparent group-hover:ring-[#D4AF37]/50 rounded-2xl transition-all" />
              </motion.figure>
            );
          })}
        </div>
      </div>
    </section>
  );
}
